import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, Btn, Card, Modal, Input, Select, Badge, Textarea } from "@/components/UI";
import { FileSignature, Upload, FilePlus2, Eye, Archive, RotateCcw, Send } from "lucide-react";

export const DOC_TYPE_LABEL = {
  contract_generated: "Sözleşme (Oluşturuldu)",
  contract_signed: "İmzalı Sözleşme Aslı",
  other: "Diğer Belge",
};
const DOC_LEVEL = { contract_generated: "attention", contract_signed: "ok", other: "inactive" };
const fmtDate = (s) => (s ? new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—");

function ContractModal({ customerId, open, onClose, onDone }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setF(null); api.get(`/customers/${customerId}/contracts/prefill`).then(setF).catch((e) => { alert(e.response?.data?.detail || "Sözleşme bilgileri alınamadı"); onClose(); }); }
  }, [open, customerId]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      const { contract_no, brand_options, template, ...body } = f;
      const doc = await api.post(`/customers/${customerId}/contracts`, { ...body, include_stamp: f.include_stamp !== false });
      onDone(doc);
    } catch (e) { alert(e.response?.data?.detail || "Sözleşme oluşturulamadı"); }
    setBusy(false);
  };
  return (
    <Modal open={open} onClose={onClose} title="Fason İmalat Sözleşmesi Oluştur" size="lg">
      {!f ? <div className="text-sm text-slate-500">Yükleniyor...</div> : (
        <div className="space-y-3" data-testid="contract-form">
          <div className="text-xs rounded-md bg-amber-50 text-amber-800 border border-amber-200 p-2">
            Şablon <b data-testid="contract-template-rev">{f.template.revision}</b> metni aynen korunur; yalnız aşağıdaki alanlar PDF üzerine yazılır.
            Buradaki değişiklikler müşteri kartını <b>değiştirmez</b>. Sözleşme No kayıtta otomatik verilir (tahmini: <span className="font-mono">{f.contract_no}</span>).
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Sözleşme Tarihi *" value={f.contract_date} onChange={set("contract_date")} data-testid="contract-date"/>
            <div>
              <Input label="Marka" value={f.brand_name} onChange={set("brand_name")} list="contract-brand-list" data-testid="contract-brand"/>
              <datalist id="contract-brand-list">{f.brand_options.map(b => <option key={b} value={b}/>)}</datalist>
            </div>
            <div className="md:col-span-2"><Input label="Müşteri Ticari Unvanı *" value={f.customer_name} onChange={set("customer_name")} data-testid="contract-customer-name"/></div>
            <div className="md:col-span-2"><Textarea label="Müşteri Adresi" value={f.customer_address} onChange={set("customer_address")} data-testid="contract-customer-address"/></div>
            <Input label="Müşteri Yetkilisi" value={f.customer_signatory} onChange={set("customer_signatory")} data-testid="contract-customer-signatory"/>
            <Input label="Meta Pharma Yetkilisi" value={f.meta_signatory} onChange={set("meta_signatory")} data-testid="contract-meta-signatory"/>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input type="checkbox" checked={f.include_stamp !== false} onChange={set("include_stamp")} data-testid="contract-include-stamp"/>
            Meta Pharma kaşe/imzasını bas
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
            <Btn onClick={submit} disabled={busy} data-testid="contract-generate-submit"><FileSignature size={14}/> {busy ? "Oluşturuluyor..." : "Oluştur"}</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

function UploadModal({ customerId, mode, contracts, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [linked, setLinked] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFile(null); setTitle(""); setLinked(contracts[0]?.id || ""); }, [mode, contracts]);
  const submit = async () => {
    if (!file) { alert("Dosya seçin"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("doc_type", mode); fd.append("title", title);
    if (mode === "contract_signed") fd.append("linked_contract_id", linked);
    try { await api.postForm(`/customers/${customerId}/documents/upload`, fd); onDone(); }
    catch (e) { alert(e.response?.data?.detail || "Yükleme başarısız"); }
    setBusy(false);
  };
  return (
    <Modal open={!!mode} onClose={onClose} title={mode === "contract_signed" ? "İmzalı Sözleşme Aslını Yükle" : "Diğer Belge Yükle"}>
      <div className="space-y-3">
        {mode === "contract_signed" && (
          <Select label="Bağlı Sözleşme" value={linked} onChange={e => setLinked(e.target.value)} data-testid="upload-linked-contract">
            <option value="">— Bağlantısız —</option>
            {contracts.map(c => <option key={c.id} value={c.id}>{c.contract_no} · {c.template_revision}</option>)}
          </Select>
        )}
        <Input label="Başlık (opsiyonel)" value={title} onChange={e => setTitle(e.target.value)} data-testid="upload-doc-title"/>
        <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="upload-doc-file"
          accept={mode === "contract_signed" ? ".pdf,image/*" : undefined}/>
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={submit} disabled={busy} data-testid="upload-doc-submit"><Upload size={14}/> {busy ? "Yükleniyor..." : "Yükle"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function SendModal({ doc, onClose }) {
  const [d, setD] = useState(null);
  const [channel, setChannel] = useState("email");
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (doc) {
      setD(null);
      api.get(`/customer-documents/${doc.id}/send-defaults`).then((r) => { setD(r); setChannel("email"); setRecipient(r.email); })
        .catch((e) => { alert(e.response?.data?.detail || "Bilgiler alınamadı"); onClose(); });
    }
  }, [doc]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (c) => { setChannel(c); setRecipient(c === "email" ? d.email : d.telegram_chat_id); };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/customer-documents/${doc.id}/send`, { channel, recipient, subject: d.subject, body: d.body });
      alert("Gönderildi"); onClose();
    } catch (e) { alert(e.response?.data?.detail || "Gönderilemedi"); }
    setBusy(false);
  };
  return (
    <Modal open={!!doc} onClose={onClose} title={`Gönder — ${doc?.title || ""}`}>
      {!d ? <div className="text-sm text-slate-500">Yükleniyor...</div> : (
        <div className="space-y-3" data-testid="send-doc-form">
          <div className="flex gap-2">
            {[["email", "E-posta"], ["telegram", "Telegram"]].map(([k, l]) => (
              <Btn key={k} size="sm" variant={channel === k ? "primary" : "secondary"} onClick={() => pick(k)} data-testid={`send-channel-${k}`}>{l}</Btn>
            ))}
          </div>
          <Input label={channel === "email" ? "Alıcı E-posta" : "Telegram Chat ID"} value={recipient || ""} onChange={e => setRecipient(e.target.value)} data-testid="send-recipient"/>
          {channel === "email" && (<>
            <Input label="Konu" value={d.subject} onChange={e => setD({ ...d, subject: e.target.value })} data-testid="send-subject"/>
            <Textarea label="Mesaj" rows={5} value={d.body} onChange={e => setD({ ...d, body: e.target.value })} data-testid="send-body"/>
          </>)}
          {d.history.length > 0 && (
            <div className="text-xs text-slate-500 border-t pt-2" data-testid="send-history">
              <div className="font-semibold mb-1">Gönderim Geçmişi</div>
              {d.history.slice(0, 5).map(h => (
                <div key={h.id}>{fmtDate(h.sent_at)} · {h.channel} · {h.recipient} · <span className={h.status === "ok" ? "text-emerald-600" : "text-red-600"}>{h.status === "ok" ? "Gönderildi" : "Hata"}</span></div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
            <Btn onClick={submit} disabled={busy || !recipient} data-testid="send-doc-submit"><Send size={14}/> {busy ? "Gönderiliyor..." : "Gönder"}</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function CustomerDocuments({ customerId }) {
  const [rows, setRows] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState(null);
  const [sendDoc, setSendDoc] = useState(null);
  const load = useCallback(() => {
    api.get(`/customers/${customerId}/documents`, { include_deleted: showArchived }).then(setRows);
  }, [customerId, showArchived]);
  useEffect(() => { load(); }, [load]);
  const contracts = rows.filter(r => r.doc_type === "contract_generated" && !r.deleted_at);
  const open = (r) => api.download(`/customer-documents/${r.id}/file`, r.filename || `${r.title}.pdf`);
  const archive = async (r) => {
    if (!window.confirm(`"${r.title}" arşive alınsın mı? (Kalıcı silinmez, geri alınabilir.)`)) return;
    try { await api.del(`/customer-documents/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail || "İşlem başarısız"); }
  };
  const restore = async (r) => { await api.post(`/customer-documents/${r.id}/restore`); load(); };
  return (
    <>
    <Card className="p-4 mb-4" data-testid="customer-documents">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>Belgeler ({rows.filter(r => !r.deleted_at).length})</div>
        <div className="flex flex-wrap gap-2">
          <Btn size="sm" onClick={() => setContractOpen(true)} data-testid="btn-generate-contract"><FileSignature size={12}/> Sözleşme Oluştur</Btn>
          <Btn size="sm" variant="secondary" onClick={() => setUploadMode("contract_signed")} data-testid="btn-upload-signed"><Upload size={12}/> İmzalı Asıl Yükle</Btn>
          <Btn size="sm" variant="secondary" onClick={() => setUploadMode("other")} data-testid="btn-upload-other"><FilePlus2 size={12}/> Diğer Belge</Btn>
          <label className="flex items-center gap-1 text-xs text-slate-500 ml-1">
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-testid="toggle-archived-docs"/> Arşivi göster
          </label>
        </div>
      </div>
      <Table testid="customer-documents-table" columns={[
        { label: "Tür", render: r => <Badge level={r.deleted_at ? "inactive" : DOC_LEVEL[r.doc_type]}>{DOC_TYPE_LABEL[r.doc_type]}</Badge> },
        { label: "Başlık", render: r => <span className={r.deleted_at ? "line-through text-slate-400" : ""}>{r.title}</span> },
        { label: "Sözleşme No", render: r => <span className="font-mono text-xs">{r.contract_no || "—"}</span> },
        { label: "Şablon", render: r => r.template_revision || "—" },
        { label: "Tarih", render: r => <span className="text-xs">{fmtDate(r.created_at)}</span> },
        { label: "Ekleyen", render: r => <span className="text-xs">{r.created_by}</span> },
        { label: "İşlem", render: r => (
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Btn size="sm" variant="secondary" onClick={() => open(r)} data-testid={`doc-open-${r.id}`}><Eye size={12}/></Btn>
            {r.deleted_at
              ? <Btn size="sm" variant="secondary" onClick={() => restore(r)} data-testid={`doc-restore-${r.id}`}><RotateCcw size={12}/></Btn>
              : <>
                <Btn size="sm" variant="secondary" onClick={() => setSendDoc(r)} data-testid={`doc-send-${r.id}`}><Send size={12}/></Btn>
                <Btn size="sm" variant="danger" onClick={() => archive(r)} data-testid={`doc-archive-${r.id}`}><Archive size={12}/></Btn>
              </>}
          </div>
        ) },
      ]} rows={rows} empty="Belge yok"/>
    </Card>
      {/* Modals rendered outside Card: Card's backdrop-filter would otherwise trap position:fixed overlay */}
      <ContractModal customerId={customerId} open={contractOpen} onClose={() => setContractOpen(false)}
        onDone={(doc) => { setContractOpen(false); load(); open(doc); }}/>
      <UploadModal customerId={customerId} mode={uploadMode} contracts={contracts}
        onClose={() => setUploadMode(null)} onDone={() => { setUploadMode(null); load(); }}/>
      <SendModal doc={sendDoc} onClose={() => setSendDoc(null)}/>
    </>
  );
}
